<template>
  <div>
    <section>
      <h6>Desktop image</h6>

      <div v-if="hasImage('image_desktop')" class="responsive-image-preview mb-3">
        <img
          :src="imageUrl('image_desktop')"
          :srcset="imageSrcset('image_desktop')"
          sizes="(max-width: 575px) 100vw, 450px"
          alt="Desktop image preview"
        >
      </div>

      <b-form-file
        v-model="image_desktop"
        ref="image_desktop"
        class="d-none"
        accept="image/*"
        @input="uploadImage($event, 'image_desktop')"
      />

      <div class="mb-2">
        <small class="text-red">
          <strong>Minimum width: 1372px. Recommended width: 2744px.</strong>
        </small>
        <small class="d-block text-muted">
          The image will be cropped to the 1372 × 335 aspect ratio.
        </small>
      </div>

      <div>
        <b-button
          variant="default"
          :disabled="Boolean(uploadingImage)"
          @click="pickImage('image_desktop')"
        >
          {{ uploadingImage === 'image_desktop' ? 'Uploading…' : 'Upload Desktop Image' }}
        </b-button>

        <b-button
          v-if="hasImage('image_desktop')"
          variant="danger"
          class="ml-2"
          :disabled="Boolean(uploadingImage)"
          @click="deleteImage('image_desktop')"
        >
          Delete Image
        </b-button>
      </div>
    </section>

    <hr />

    <section>
      <h6>Mobile image</h6>

      <div v-if="hasImage('image_mobile')" class="responsive-image-preview mb-3">
        <img
          :src="imageUrl('image_mobile')"
          :srcset="imageSrcset('image_mobile')"
          sizes="(max-width: 575px) 100vw, 450px"
          alt="Mobile image preview"
        >
      </div>

      <b-form-file
        v-model="image_mobile"
        ref="image_mobile"
        class="d-none"
        accept="image/*"
        @input="uploadImage($event, 'image_mobile')"
      />

      <div class="mb-2">
        <small class="text-red">
          <strong>Minimum width: 770px.</strong>
        </small>
        <small class="d-block text-muted">
          The image will be cropped to the 385 × 204 aspect ratio.
        </small>
      </div>

      <div>
        <b-button
          variant="default"
          :disabled="Boolean(uploadingImage)"
          @click="pickImage('image_mobile')"
        >
          {{ uploadingImage === 'image_mobile' ? 'Uploading…' : 'Upload Mobile Image' }}
        </b-button>

        <b-button
          v-if="hasImage('image_mobile')"
          variant="danger"
          class="ml-2"
          :disabled="Boolean(uploadingImage)"
          @click="deleteImage('image_mobile')"
        >
          Delete Image
        </b-button>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ResponsiveImagePicker',
  props: {
    imageDesktop: { type: Object, default: null },
    imageMobile: { type: Object, default: null },
    uploadFn: { type: Function, required: true },
    deleteFn: { type: Function, required: true },
  },
  data() {
    return {
      image_desktop: null,
      image_mobile: null,
      uploadingImage: null,
      savedImages: {
        image_desktop: this.imageDesktop,
        image_mobile: this.imageMobile,
      },
    }
  },
  watch: {
    imageDesktop(image) {
      this.$set(this.savedImages, 'image_desktop', image)
    },
    imageMobile(image) {
      this.$set(this.savedImages, 'image_mobile', image)
    },
  },
  methods: {
    pickImage(imageType) {
      this.$refs[imageType].$el.querySelector('input').click()
    },
    async uploadImage(image, imageType) {
      if (!image) return

      this.uploadingImage = imageType

      try {
        const savedImage = await this.uploadFn(image, imageType)
        this.$set(this.savedImages, imageType, savedImage)

        this.$bvToast.toast('New image was successfully added.', {
          title: 'Image Uploaded.',
          variant: 'primary',
        })
      } catch (error) {
        this.$bvToast.toast(error.message, { title: 'Something went wrong.', variant: 'danger' })
      } finally {
        this[imageType] = null
        this.uploadingImage = null
      }
    },
    async deleteImage(imageType) {
      this.uploadingImage = imageType

      try {
        await this.deleteFn(imageType)
        this.$set(this.savedImages, imageType, null)

        this.$bvToast.toast('Image was successfully deleted.', {
          title: 'Image Deleted.',
          variant: 'primary',
        })
      } catch (error) {
        this.$bvToast.toast(error.message, { title: 'Something went wrong.', variant: 'danger' })
      } finally {
        this[imageType] = null
        this.uploadingImage = null
      }
    },
    hasImage(imageType) {
      const image = this.savedImages[imageType]

      return Boolean(image && (image.url || image.srcset))
    },
    imageUrl(imageType) {
      const image = this.savedImages[imageType]

      if (!image) return ''
      if (image.url) return image.url

      return image.srcset ? image.srcset.split(',')[0].trim().split(' ')[0] : ''
    },
    imageSrcset(imageType) {
      return this.savedImages[imageType]?.srcset || null
    },
  },
}
</script>

<style scoped>
.responsive-image-preview {
  overflow: hidden;
  background: #f2f3f4;
  border-radius: 0.25rem;
}

.responsive-image-preview img {
  display: block;
  width: 100%;
  height: auto;
}
</style>
